import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SPECTRUM_CATEGORIES = [
  {
    id: 'all',
    label: 'All 59+ Cancers Covered',
    count: '59+',
    desc: 'Broad coverage across common solid tumors, hematologic conditions, and rare pediatric and adult cancers.',
    examples: ['Breast', 'Lung', 'Colorectal', 'DIPG', 'Chordoma', 'GIST', 'Angiosarcoma', 'ATRT']
  },
  {
    id: 'ultra',
    label: 'Ultra-Rare Cancers',
    count: '14',
    desc: 'Rare pediatric and adult cancers where finding high-volume cancer centers and clinical trial options matters most.',
    examples: ['DIPG', 'ATRT', 'SCCOHT', 'BPDCN', 'NUT Carcinoma', 'ETMR']
  },
  {
    id: 'very',
    label: 'Very Rare Malignancies',
    count: '12',
    desc: 'Complex conditions that require multidisciplinary surgery, targeted therapy, and expert second opinions.',
    examples: ['Chordoma', 'Angiosarcoma', 'Uveal Melanoma', 'Anaplastic Thyroid', 'Clear Cell Sarcoma']
  },
  {
    id: 'rare',
    label: 'Rare Tumors',
    count: '13',
    desc: 'Uncommon cancers with targeted molecular pathways, genomic markers, and dedicated treatment protocols.',
    examples: ['GIST', 'Mesothelioma', 'Ewing Sarcoma', 'DSRCT', 'Histiocytic Sarcoma']
  },
  {
    id: 'common',
    label: 'Common & Solid Tumors',
    count: '22+',
    desc: 'Common cancers supported by standard-of-care NCCN and ASCO clinical treatment guidelines.',
    examples: ['Breast (HR+/HER2-/TNBC)', 'Non-Small Cell Lung', 'Colorectal Adeno', 'Multiple Myeloma', 'Prostate']
  }
];

const CLINICAL_PILLARS = [
  {
    idx: '01 // TREATMENT PROTOCOLS',
    category: 'Treatments & Transplants',
    title: 'Chemotherapy, Immunotherapy & Stem Cell Transplants',
    description: 'Clear explanations of chemotherapy regimens, immunotherapy, radiation schedules, and targeted molecular therapies. Full support for bone marrow and stem cell transplants, including conditioning protocols, engraftment milestones, GVHD monitoring, and post-transplant recovery.',
    tags: ['Chemotherapy', 'Immunotherapy', 'CAR-T Cell', 'Stem Cell Transplant', 'Targeted Therapies'],
    icon: (
      <svg className="w-5 h-5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-6 9h9m-9 4h9" />
      </svg>
    )
  },
  {
    idx: '02 // SIDE EFFECTS & COMFORT',
    category: 'Daily Comfort & Relief',
    title: 'Managing Side Effects at Home',
    description: 'Practical, evidence-based guidance for neuropathy, nausea, taste disruption, stoma care, and fatigue. Safe advice on nutrition during infusion cycles, infection precautions between treatments, and what to watch for when a fever strikes at 3:00 AM.',
    tags: ['Peripheral Neuropathy', 'Neutropenic Precautions', 'Nutritional Advice', 'Stoma Care', 'Managing Fatigue'],
    icon: (
      <svg className="w-5 h-5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
      </svg>
    )
  },
  {
    idx: '03 // INSURANCE & COSTS',
    category: 'Costs & Coverage',
    title: 'Insurance Navigation & Transparent Costs',
    description: 'Clear guidance on commercial health plans, Medicare Part B and D, prior authorization requirements, and copay assistance programs in the US. Upfront cost estimates for procedures, plus guidance on hospital matching and Indian health schemes when cross-border care is needed.',
    tags: ['Medicare Part B & D', 'Prior Authorization', 'Procedure Costs', 'US Hospital Matching', 'Copay Assistance'],
    icon: (
      <svg className="w-5 h-5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  {
    idx: '04 // RARE CANCER CARE',
    category: 'Rare & Ultra-Rare Cancers',
    title: 'Dedicated Guidance for Rare Cancers',
    description: 'Specialized clinical information for more than 50 rare and ultra-rare conditions. Plain explanations of molecular pathology reports, genetic alterations such as NTRK or BRAF, and guidance on locating high-volume centers and active clinical trials.',
    tags: ['50+ Rare Cancers', 'Biomarker Guidance', 'Clinical Trial Matching', 'High-Volume Centers'],
    icon: (
      <svg className="w-5 h-5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
      </svg>
    )
  },
  {
    idx: '05 // CAREGIVER SUPPORT',
    category: 'Caregiver Support',
    title: 'Built for the Family Members Carrying the Care',
    description: 'Designed for the relatives and friends coordinating daily life: translating confusing doctor notes, keeping medication schedules organized, knowing when a symptom requires urgent emergency care, and caring for yourself through the stress.',
    tags: ['Portal Note Explanations', 'Urgent Warning Signs', 'Medication Schedules', 'Caregiver Well-Being'],
    icon: (
      <svg className="w-5 h-5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    )
  },
  {
    idx: '06 // CLINICAL GUARDRAILS',
    category: 'Medical Boundaries',
    title: 'Strict Medical Guardrails & Patient Safety',
    description: 'Dedicated entirely to supportive care navigation. Vani never diagnoses, prescribes, or changes a dose. Every response works alongside your oncologist’s treatment plan, helping you ask clearer, more informed questions during your clinic visits.',
    tags: ['Non-Diagnostic Boundary', 'Doctor-Aligned', 'Urgent Triage Alerts', 'HIPAA-Ready'],
    icon: (
      <svg className="w-5 h-5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    )
  }
];

export default function ClinicalScopeBento({ stats }) {
  const [selectedSpectrum, setSelectedSpectrum] = useState('all');
  const currentCategory = SPECTRUM_CATEGORIES.find((c) => c.id === selectedSpectrum) || SPECTRUM_CATEGORIES[0];

  return (
    <section id="scope" className="byo-bento-section byo-scope-bg">
      <div className="vani-wrap">
        {/* Apple/YC Style Section Header */}
        <div className="byo-bento-header">
          <div className="byo-pill-badge">
            <span className="byo-badge-dot" />
            <span>CLINICAL COVERAGE</span>
            <span className="byo-badge-sep">/</span>
            <span className="byo-pill-highlight">290+ CANCER CENTERS & PROTOCOLS</span>
          </div>
          <h2 className="byo-bento-title">
            Support for Every Part of Your Cancer Journey
          </h2>
          <p className="byo-bento-subtitle">
            From managing chemotherapy side effects and daily comfort at home to understanding insurance coverage, specialist referrals, and rare cancer care, organized with clinical rigor.
          </p>
        </div>

        {/* Interactive Scope Spectrum Selector Bar */}
        <div className="byo-spectrum-container">
          <div className="byo-spectrum-tabs" role="tablist" aria-label="Select cancer coverage category">
            {SPECTRUM_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedSpectrum(cat.id)}
                className={`byo-spectrum-tab ${selectedSpectrum === cat.id ? 'is-active' : ''}`}
              >
                <span className="byo-tab-count">{cat.count}</span>
                <span className="byo-tab-label">{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Active Category Sub-Detail Banner */}
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedSpectrum}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.16 }}
              className="byo-spectrum-detail"
            >
              <div className="byo-spectrum-desc">{currentCategory.desc}</div>
              <div className="byo-spectrum-chips">
                <span className="byo-chip-lead">Covered Protocols:</span>
                {currentCategory.examples.map((ex, i) => (
                  <span key={i} className="byo-spectrum-chip">
                    {ex}
                  </span>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* 6-Card Bento Matrix */}
        <div className="byo-scope-grid">
          {CLINICAL_PILLARS.map((pillar, index) => (
            <div key={index} className="byo-scope-card group">
              <div className="byo-scope-card-top">
                <div className="byo-scope-icon-wrap">
                  {pillar.icon}
                </div>
                <div className="flex flex-col items-end">
                  <span className="byo-card-idx">{pillar.idx}</span>
                  <span className="byo-scope-cat-badge">{pillar.category}</span>
                </div>
              </div>

              <div className="byo-scope-card-body">
                <h3 className="byo-scope-card-title">{pillar.title}</h3>
                <p className="byo-scope-card-desc">{pillar.description}</p>
              </div>

              <div className="byo-scope-tags">
                {pillar.tags.map((tag, tIdx) => (
                  <span key={tIdx} className="byo-scope-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
